import cn from 'classnames'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { IconButton, Tooltip, Typography } from '@material-ui/core'
import { PriorityHighRounded } from '@material-ui/icons'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import CloseIcon from '@material-ui/icons/Close'
import RepeatIcon from '@material-ui/icons/Repeat'
import { ModalContext } from '@root-gipro/context/modal-context'
import UncReplaceModal from '@root-gipro/modules/userProjects/components/Project/UncTable/UncReplaceModal'
import { WrapperCell } from '@root-gipro/modules/userProjects/components/Project/UncTable/WrapperCell'
import { Cell } from '@root-gipro/modules/userProjects/components/Project/UncTable/Сell'
import Voltage from '@root-gipro/modules/userProjects/components/Project/Voltage'
import { cellTags } from '@root-gipro/modules/userProjects/models/consts/unc-cell-tags.const'
import { gridTemplateColumns } from '@root-gipro/modules/userProjects/models/consts/unc-thead.const'
import { CellsProps } from '@root-gipro/modules/userProjects/models/interfaces/project.props'
import {
	changeCounter,
	deleteUnc,
	editPositionUnc,
	setActiveTotal,
	setActiveUncs,
} from '@root-gipro/modules/userProjects/store/actions'
import { addActiveUnc } from '@root-gipro/modules/userProjects/utils/helpers'
import { removeSpaceFromNumber } from '@root-gipro/modules/userProjects/utils/removeSpaceFromNumber'
import styles from '@root-gipro/shared/Table/styles/Table.module.scss'
import { IRegion, IUnc } from '@root-gipro/store/interfaces'
import { IState } from '@root-gipro/store/interfaces/state'
import { intFormat, prettyCost } from '@root-gipro/utils/helpers/common'
import { useLocation } from 'react-router-dom'
import TransitionConfigurator from '../Modal/TransitionConfigurator'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

function replaceString(str: string) {
	if (str.toLowerCase().includes('погонный метр')) {
		return str.replace('погонный метр', 'пог.м')
	}
	return str
}

export const Cells: React.FC<CellsProps> = ({
	uncs,
	voltageRef,
	unc,
	index,
	currentCellTags,
	isArchived,
	currentUncSort,
	checkedTable,
}) => {
	const [uncCount, setUncCount] = useState<number>(unc.count)
	const { activeUncs, loading, projectObj, showWarningUncIds } = useSelector((state: IState) => state.userProjects)
	const { regions } = useSelector((state: IState) => state.public)
	const [isActualUncSort, setIsActualUncSort] = useState<boolean>(true)
	const { setAnchorEl, accessoryRef } = useContext(ModalContext)
	const location = useLocation()
	const [openTooltip, setOpenTooltip] = React.useState(false)
	const isCommonProjects = location.pathname.includes('common-projects')

	// Новое состояние для управления видимостью кнопок через JS
	const [showButtons, setShowButtons] = useState(false)

	const handleClose = () => {
		setOpenTooltip(false)
	}

	const handleOpen = () => {
		setOpenTooltip(true)
	}

	const [anchorElUncSort, setAnchorElUncSort] = React.useState<null | HTMLElement>(null)
	const [anchorElUncConfig, setAnchorElUncConfig] = React.useState<null | HTMLElement>(null)
	const [activeWarning, setActiveWarning] = useState<boolean>(true)

	const handleClickUncSort = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElUncSort(event.currentTarget)
	}

	const dispatch = useDispatch()
	const rowRef = useRef<HTMLInputElement>(null)

	const trClickHandler = useCallback(
		(e: React.MouseEvent<HTMLElement>, unc: IUnc) => {
			if (e.metaKey || e.ctrlKey) {
				const { clone } = addActiveUnc(activeUncs, unc)
				dispatch(setActiveUncs(clone))
			} else {
				if (activeUncs.length === 1 && activeUncs[0].id === unc.id) {
					return
				} else {
					dispatch(setActiveUncs([unc]))
				}
			}
		},
		[activeUncs, setActiveUncs]
	)
	const regionTdClicHandler = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (!isArchived) {
				e.stopPropagation()
				if (e.metaKey || e.ctrlKey) {
					const { clone } = addActiveUnc(activeUncs, unc)
					dispatch(setActiveUncs(clone))
				} else {
					if (!activeUncs.some(elem => elem.id === unc.id)) {
						dispatch(setActiveUncs([unc]))
					}
				}
				const { index } = addActiveUnc(activeUncs, unc)
				if (accessoryRef) accessoryRef.current = String(index)
				setAnchorEl!(e.currentTarget)
				if (rowRef.current) rowRef.current.focus()
			}
		},
		[activeUncs, unc]
	)

	function getRegionZoneNames(zoneIds: string | string[] | undefined): string {
		if (!zoneIds) return ''

		const idsArray = Array.isArray(zoneIds) ? zoneIds : [zoneIds]
		const names: string[] = []

		for (const region of regions) {
			for (const zone of region.region_zones || []) {
				if (idsArray.includes(zone.id)) {
					names.push(zone.name)
				}
			}
		}

		return names.join(', ')
	}

	const commentTdClickHandler = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.stopPropagation()
			if (e.metaKey || e.ctrlKey) {
				const { clone } = addActiveUnc(activeUncs, unc)
				dispatch(setActiveUncs(clone))
			} else {
				if (!activeUncs.some(elem => elem.id === unc.id)) {
					dispatch(setActiveUncs([unc]))
				}
				const { index } = addActiveUnc(activeUncs, unc)
				if (accessoryRef) accessoryRef.current = String(index)
				setAnchorEl!(e.currentTarget)
				if (rowRef.current) rowRef.current.focus()
			}
		},
		[activeUncs, unc]
	)

	const region: IRegion | undefined = useMemo(() => {
		return regions?.find(elem => +elem.id === +unc.regionId)
	}, [unc, regions, unc.regionId])

	useEffect(() => {
		setIsActualUncSort(
			isCommonProjects || Number(currentUncSort) == 2018 || Number(currentUncSort) === Number(unc.unc_sort)
		)

		setUncCount(unc.count)
	}, [unc, currentUncSort])

	const checkDis = (value: number) => {
		if (value === 8) {
			return true
		} else if (value <= 5) {
			return false
		}
		return true
	}

	const handlerCloseConfig = () => {
		setAnchorElUncConfig!(null!)
	}

	const handlerOpenConfig = (e: any) => {
		setAnchorElUncConfig!(e.currentTarget)
	}
	const handlerTransitionConfig = (e: any) => {
		setAnchorElUncConfig!(null!)
		commentTdClickHandler(e)
	}

	// Функции для управления отображением кнопок с помощью JavaScript
	const handleMouseEnter = () => {
		setShowButtons(true)
	}

	const handleMouseLeave = () => {
		setShowButtons(false)
	}
	const isDisabled = checkDis(checkedTable)

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: unc.id.toString(),
		disabled: isDisabled,
	})

	return (
		<Tooltip
			open={openTooltip && !isActualUncSort}
			onClose={handleClose}
			onOpen={handleOpen}
			title={<Typography>Расценка исключена из справочника УНЦ 2024, подлежит замене</Typography>}
		>
			<div
				tabIndex={+unc.id}
				className={cn(styles.rowWrapper)}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={e => trClickHandler(e, unc)}
			>
				{!checkDis(checkedTable) && (
					<div
						className={cn(
							styles.buttonGroupLeft,
							{ [styles.onlyUp]: index === uncs.length - 1 },
							{ [styles.onlyDown]: index === 0 }
						)}
						style={{
							opacity: showButtons ? 1 : 0,
							zIndex: 1000,
							position: 'absolute',
							left: '-64px',
							top: '4px',
						}}
					>
						<IconButton
							className={cn(styles.iconWrap, styles.swapUpBtn)}
							disabled={loading || index === 0}
							onClick={e => {
								e.stopPropagation()

								const isMulti = activeUncs.some(item => item.id === unc.id)
								const idsToMove = isMulti ? activeUncs.map(item => item.id.toString()) : [unc.id.toString()]

								dispatch(
									editPositionUnc({
										ids: idsToMove,
										destination: {
											index: index - 1,
											droppableId: `tbody ${checkedTable}`,
										},
									})
								)
							}}
						>
							<ArrowUpwardIcon className={cn(styles.icon, styles.swapUpIcon)} />
						</IconButton>

						<IconButton
							className={cn(styles.iconWrap, styles.swapDownBtn)}
							disabled={loading || index === uncs.length - 1}
							onClick={e => {
								e.stopPropagation()

								const isMulti = activeUncs.some(item => item.id === unc.id)
								const idsToMove = isMulti ? activeUncs.map(item => item.id.toString()) : [unc.id.toString()]

								dispatch(
									editPositionUnc({
										ids: idsToMove,
										destination: {
											index: index + 1,
											droppableId: `tbody ${checkedTable}`,
										},
									})
								)
							}}
						>
							<ArrowDownwardIcon className={cn(styles.icon)} />
						</IconButton>
					</div>
				)}
				<div
					onClick={e => trClickHandler(e, unc)}
					key={unc.id + 1}
					className={cn(styles.rowItem, {
						[styles.activeRow]: activeUncs.some(active => active.id === unc.id),
						[styles.rowWrapperNonRelevant]: !isActualUncSort,
						[styles.lastRowItem]: index === uncs.length - 1,
					})}
				>
					<div
						className={cn(styles.grid, styles.alignTheElement)}
						style={{
							gridTemplateColumns: gridTemplateColumns,
							gap: '8px',
							padding: '8px',
						}}
					>
						<WrapperCell currentCellTags={currentCellTags}>
							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<div className={cn(styles.tableCell, styles.textAlignCenter)}>{index + 1}</div>
							</Cell>

				
							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<div
									className={cn(styles.tableCell)}
									ref={setNodeRef}
									style={{
										transform: CSS.Transform.toString(transform),
										transition,
										cursor: !isDragging && isDisabled ? 'default' : 'grab',
										position: 'relative',
										zIndex: isDragging ? 999 : 'auto',
									}}
									{...(isDisabled ? {} : attributes)}
									{...(isDisabled ? {} : listeners)}
								>
									{unc?.uncCell?.code}
									{!showWarningUncIds.includes(unc.id) &&
									!unc?.kf_confirmed &&
									unc.uncMain?.complex_coeff &&
									unc.uncMain?.complex_coeff?.length > 0 ? (
										<div
											className={cn(styles.tableCellWarning)}
											data-popup='config'
											onClick={e => {
												handlerOpenConfig(e)
											}}
										>
											<PriorityHighRounded style={{ color: 'red', fontSize: '20px' }} />
										</div>
									) : (
										''
									)}
								</div>
							</Cell>

							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<div style={{ display: 'flex', justifyContent: 'space-between' }}>
									<Tooltip
										key={unc.uncMain?.name}
										title={unc.uncMain?.name}
										enterDelay={500}
										enterNextDelay={500}
										placement='top'
									>
										<div className={cn(styles.tableCell)}>{unc.uncMain?.name}</div>
									</Tooltip>
									<div
										data-popup='comment'
										onClick={commentTdClickHandler}
										style={{ cursor: 'pointer' }}
										className={cn(styles.tableCell)}
									>
										<span className='comment-icon'></span>
									</div>
								</div>
							</Cell>
							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<div className={cn(styles.tableCell)}>
									<input
										onChange={e => {
											setUncCount(intFormat(e.currentTarget.value))
										}}
										disabled={isArchived}
										onBlur={e => {
											dispatch(changeCounter(unc.id, removeSpaceFromNumber(e.currentTarget.value)))
										}}
										style={{ borderColor: uncCount ? '#e0e0e0' : '#d33131' }}
										className={styles.uncCounter}
										value={uncCount ? intFormat(uncCount) : 0}
									/>
								</div>
							</Cell>
							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<Tooltip
									key={unc?.unit ?? unc.uncCell?.unit ?? unc.uncMain?.unit}
									title={unc?.unit ?? unc.uncCell?.unit ?? unc.uncMain?.unit}
									enterDelay={500}
									enterNextDelay={500}
									placement='top'
								>
									<div className={cn(styles.tableCell, styles.textAlignCenter)}>
										{replaceString(unc?.unit ?? unc.uncCell?.unit ?? unc.uncMain?.unit)}
									</div>
								</Tooltip>
							</Cell>
							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<Tooltip
									key={unc.uncCell?.parameter}
									title={unc.uncCell?.parameter}
									enterDelay={500}
									enterNextDelay={500}
									placement='top'
								>
									<div className={cn(styles.tableCell)}>{unc.uncCell?.parameter}</div>
								</Tooltip>
							</Cell>

							<Cell cellTags={[cellTags[0], cellTags[1]]}>
								<div className={cn(styles.tableCell, styles.accent, styles.textAlignCenter)}>
									<Voltage voltageRef={voltageRef} dataAttr='volt-unc-table' unc={unc} isArchived={isArchived} />
								</div>
							</Cell>

							<Cell cellTags={[cellTags[0]]}>
								<div className={cn(styles.tableCell)}>
									{prettyCost(unc.cost / 1000)}
									{unc.uncMain?.code === 'С1' ? (
										<span className='currency'> м²</span>
									) : (
										<span className='currency'> тыс&nbsp;₽</span>
									)}
								</div>
							</Cell>
							<Cell cellTags={[cellTags[0]]}>
								<Tooltip
									key={unc.region_zone_id ? getRegionZoneNames(unc.region_zone_id) : region?.name}
									title={
										unc.region_zone_id ? getRegionZoneNames(unc?.region_zone_id) : region?.name ? region?.name : ''
									}
									enterDelay={500}
									enterNextDelay={500}
									placement='top'
								>
									<div
										data-popup={'region'}
										style={{ cursor: 'pointer' }}
										data-region={unc.id}
										onClick={regionTdClicHandler}
										className={cn(styles.tableCell, styles.regionName)}
									>
										<span>{unc.reg_k === 1 ? '1.00' : unc.reg_k ? unc.reg_k.toFixed(2) : '1.00'}</span>
										<span className={cn(styles.accent, 'ml-4')}>
											{unc.region_zone_id ? getRegionZoneNames(unc.region_zone_id) : region?.name}
										</span>
									</div>
								</Tooltip>
							</Cell>
							<Cell cellTags={[cellTags[0]]}>
								<div className={cn(styles.totalCost, styles.tableCell)}>
									{prettyCost(unc.totalCost / 1000)}
									{unc.uncMain?.code === 'С1' ? (
										<span className='currency'> м²</span>
									) : (
										<span className='currency'> тыс&nbsp;₽</span>
									)}
								</div>
							</Cell>
							<Cell cellTags={[cellTags[1]]}>
								<div className={cn(styles.tableCell)}>
									{' '}
									{prettyCost(unc.cost / 1000)}
									{unc.uncMain?.code === 'С1' ? (
										<span className='currency'> м²</span>
									) : (
										<span className='currency'> тыс&nbsp;₽</span>
									)}
								</div>
							</Cell>
							<Cell cellTags={[cellTags[1]]}>
								<Tooltip
									key={unc.region_zone_id ? getRegionZoneNames(unc.region_zone_id) : region?.name}
									title={
										unc.region_zone_id ? getRegionZoneNames(unc?.region_zone_id) : region?.name ? region?.name : ''
									}
									enterDelay={500}
									enterNextDelay={500}
									placement='top'
								>
									<div
										data-popup={unc.reg_k && unc.uncMain?.code !== 'Б1' ? 'region' : null}
										style={{
											cursor: unc.reg_k && unc.uncMain?.code !== 'Б1' ? 'pointer' : 'default',
										}}
										data-region={unc.regionId}
										onClick={regionTdClicHandler}
										className={cn(styles.tableCell, styles.regionName)}
									>
										<span>{unc.reg_k ? unc.reg_k : '1.00'}</span>
										<span className={cn(styles.accent, 'ml-4')}>
											{unc.region_zone_id ? getRegionZoneNames(unc.region_zone_id) : region?.name}
										</span>
									</div>
								</Tooltip>
							</Cell>
							<Cell cellTags={[cellTags[1]]}>
								<div className={cn(styles.tableCell)}>
									{prettyCost(unc.totalCost / 1000)}
									{unc.uncMain?.code === 'С1' ? (
										<span className='currency'> м²</span>
									) : (
										<span className='currency'> тыс&nbsp;₽</span>
									)}
								</div>
							</Cell>
						</WrapperCell>
					</div>

					{/* Кнопки управления расценкой */}
					{!isArchived && (
						<div
							className={cn(styles.buttonGroup, { [styles.isNoActualUncSort]: isActualUncSort })}
							style={{
								opacity: showButtons ? 1 : 0,
								zIndex: 1000,
								position: 'absolute',
								right: isActualUncSort ? '-30px' : '-28px',
								top: '4px',
							}}
						>
							<IconButton
								className={styles.iconWrap}
								disabled={loading}
								onClick={e => {
									e.stopPropagation()
									dispatch(deleteUnc(unc.id))
									if (activeUncs[0]?.id === unc.id) {
										dispatch(setActiveTotal(null))
									}
								}}
							>
								<CloseIcon className={cn(styles.icon, styles.delBtn)} />
							</IconButton>
							<IconButton className={cn(styles.iconWrap, styles.iconUncReplace)} onClick={handleClickUncSort}>
								<RepeatIcon className={cn(styles.icon)} />
							</IconButton>
						</div>
					)}
				</div>

				{anchorElUncSort && (
					<UncReplaceModal
						anchorEl={anchorElUncSort}
						uncCellId={unc.uncCellId}
						uncId={unc.id}
						userProjectId={projectObj.id}
						handleClose={() => setAnchorElUncSort(null)}
					/>
				)}
				{anchorElUncConfig?.dataset.popup === 'config' && (
					<TransitionConfigurator
						anchorEl={anchorElUncConfig}
						setAnchorEl={setAnchorElUncConfig}
						handlerOpen={handlerTransitionConfig}
						handlerClose={handlerCloseConfig}
						coeff={unc.uncMain?.complex_coeff}
						notes={unc.uncMain?.coeff_notes}
						transitConfig={true}
					/>
				)}
			</div>
		</Tooltip>
	)
}
